/**
 * Cookie consent helpers — read/write/clear consent + optional cookie gating.
 *
 * The `amuster_consent` cookie is strictly necessary (records the user's choice
 * so we don't re-prompt). All other listed cookies are optional and gated
 * behind the relevant consent category.
 */

import type { CookieBannerConfig } from '@/types/bannerConfig';
import { BANNER_DEFAULTS } from '@/types/bannerConfig';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CookieConsent {
  /** Bump when the cookie list changes; forces re-consent */
  version: number;
  /** ISO timestamp of the consent action */
  timestamp: string;
  /** Always true — essential is never user-togglable */
  essential: true;
  /** Nav prefs, clinic scope, tenant_key, sidebar_state */
  functional: boolean;
  /** First-party analytics (future) */
  analytics: boolean;
  /** Theme, locale (future) */
  preferences: boolean;
}

export type ConsentCategory = 'essential' | 'functional' | 'analytics' | 'preferences';

// ── Constants ──────────────────────────────────────────────────────────────────

const CONSENT_COOKIE_NAME = 'amuster_consent';
const CONSENT_VERSION = 1;

/** CNIL best-practice: 6 months */
const DEFAULT_MAX_AGE_DAYS = 180;

const MAX_AGE_SECONDS = DEFAULT_MAX_AGE_DAYS * 24 * 60 * 60;

/** Optional cookies / localStorage keys that require functional consent */
const OPTIONAL_COOKIE_NAMES = ['sidebar_state'] as const;

/** localStorage keys that require functional consent */
const OPTIONAL_LS_KEYS = ['amuster_nav_prefs', 'tenant_key'] as const;

/** Prefix-based localStorage keys (clinicScope_{tenantId}_*) */
const OPTIONAL_LS_PREFIXES = ['clinicScope_'] as const;

// ── Helpers ────────────────────────────────────────────────────────────────────

function isSecure(): boolean {
  return window.location.protocol === 'https:';
}

// ── Read ───────────────────────────────────────────────────────────────────────

export function readConsent(): CookieConsent | null {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)`),
    );
    if (!match?.[1]) return null;
    const decoded = decodeURIComponent(match[1]);
    const parsed = JSON.parse(decoded) as CookieConsent;

    // Version mismatch → treat as no consent (forces re-prompt)
    if (parsed.version !== CONSENT_VERSION) return null;

    return parsed;
  } catch {
    return null;
  }
}

// ── Write ──────────────────────────────────────────────────────────────────────

export function writeConsent(consent: CookieConsent): void {
  const value = encodeURIComponent(JSON.stringify(consent));
  const parts = [
    `${CONSENT_COOKIE_NAME}=${value}`,
    'path=/',
    `max-age=${MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  if (isSecure()) parts.push('Secure');
  document.cookie = parts.join('; ');
}

// ── Clear ──────────────────────────────────────────────────────────────────────

/**
 * Remove all optional cookies and localStorage keys.
 * Called on reject / category disable.
 */
export function clearOptionalCookies(): void {
  // Remove optional cookies by expiring them
  for (const name of OPTIONAL_COOKIE_NAMES) {
    document.cookie = `${name}=; path=/; max-age=0`;
  }

  // Remove optional localStorage keys
  for (const key of OPTIONAL_LS_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch { /* ignore */ }
  }

  // Remove prefix-based localStorage keys
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && OPTIONAL_LS_PREFIXES.some((p) => key.startsWith(p))) {
        localStorage.removeItem(key);
      }
    }
  } catch { /* ignore */ }
}

// ── Query ──────────────────────────────────────────────────────────────────────

/**
 * Check whether the user has granted consent for a given category.
 * Essential always returns true.
 */
export function hasConsent(category: ConsentCategory): boolean {
  if (category === 'essential') return true;
  const consent = readConsent();
  if (!consent) return false;
  return consent[category] === true;
}

// ── Factories ──────────────────────────────────────────────────────────────────

export function buildAcceptAll(): CookieConsent {
  return {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    essential: true,
    functional: true,
    analytics: true,
    preferences: true,
  };
}

export function buildRejectNonEssential(): CookieConsent {
  return {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    essential: true,
    functional: false,
    analytics: false,
    preferences: false,
  };
}

export function buildCustom(
  opts: Pick<CookieConsent, 'functional' | 'analytics' | 'preferences'>,
): CookieConsent {
  return {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    essential: true,
    ...opts,
  };
}

// ── Public Banner Config Fetcher ────────────────────────────────────────────

/**
 * Fetch tenant-specific banner config from the public endpoint.
 * Server resolves tenant from hostname — no tenantKey parameter needed.
 * Falls back to BANNER_DEFAULTS on any error (banner must always work).
 */
export async function fetchBannerConfig(): Promise<CookieBannerConfig> {
  try {
    const res = await fetch('/api/content/banner-config');
    if (!res.ok) return BANNER_DEFAULTS;
    const json = await res.json();
    return json.data ?? BANNER_DEFAULTS;
  } catch {
    return BANNER_DEFAULTS;
  }
}
